import Component from "@base/js/component";

/**
 * MapListTab Component
 *
 * Handles the tabbing functionality between Map View and List View
 * with responsive behavior for desktop and mobile.
 */
export default class MapListTab extends Component {
  /**
   * @constructor
   * @param {HTMLElement} element - The component's DOM element
   */
  constructor(element) {
    super(element);

    if (!element) {
      return;
    }

    // Store element explicitly to avoid 'this' context issues
    this.mapListElement = element;

    // Elements - use the stored element reference
    this.tabButtons = this.mapListElement.querySelectorAll(".tab-button");
    this.tabPanels = this.mapListElement.querySelectorAll(".tab-panel");
    this.mapTab = this.mapListElement.querySelector(".tab-map");

    // State
    this.isMobile = window.innerWidth < 768;

    // Initialize component
    this.init();
  }

  /**
   * Initialize the component
   */
  init() {
    // Safety check
    if (
      !this.mapListElement ||
      !this.tabButtons.length ||
      !this.tabPanels.length
    ) {
      return;
    }

    // Set initial state based on screen size
    const onlyOneView = this.tabPanels.length === 1;

    if (this.isMobile) {
      if (onlyOneView) {
        this.activateTab(0);
      } else {
        this.activateTab(1);
        if (this.mapTab) {
          this.mapTab.style.display = "none";
        }
      }
    } else {
      // On desktop, show map view by default
      this.activateTab(0);
    }

    // Bind event handlers
    this.bindEvents();
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Safety check
    if (!this.tabButtons.length) {
      return;
    }

    // Tab click handlers
    this.tabButtons.forEach((button) => {
      // Use arrow function to preserve 'this' context
      button.addEventListener("click", (event) => {
        this.handleTabClick(event);
      });
    });

    // Window resize handler with debounce
    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => this.handleResize(), 250);
    });

    this.initMapHoverEvents();
  }

  /**
   * Handle tab button clicks
   * @param {Event} event - Click event
   */
  handleTabClick(event) {
    const button = event.currentTarget;
    const tabIndex = parseInt(button.getAttribute("data-tab-index"), 10);

    // On mobile, don't allow switching to map view
    if (this.isMobile && tabIndex === 0) {
      return;
    }

    this.activateTab(tabIndex);
  }

  /**
   * Handle window resize events
   */
  handleResize() {
    const newIsMobile = window.innerWidth < 768;

    // Show/hide map tab based on screen size
    if (this.mapTab) {
      this.mapTab.style.display = newIsMobile ? "none" : "flex";
    }

    // If switched to mobile, show list view
    if (newIsMobile && !this.isMobile) {
      this.activateTab(1);
    }

    // Update state
    this.isMobile = newIsMobile;
  }

  /**
   * Activate the specified tab
   * @param {number} tabIndex - Index of the tab to activate
   */
  activateTab(tabIndex) {
    if (!this.tabButtons || !this.tabPanels) {
      return;
    }

    // Check if tabIndex is valid
    if (tabIndex < 0 || tabIndex >= this.tabButtons.length) {
      return;
    }

    // Update button states
    this.tabButtons.forEach((button, index) => {
      if (index === tabIndex) {
        button.classList.add("active");
        button.setAttribute("aria-selected", "true");
      } else {
        button.classList.remove("active");
        button.setAttribute("aria-selected", "false");
      }
    });

    // Update panel visibility
    this.tabPanels.forEach((panel, index) => {
      if (index === tabIndex) {
        panel.classList.remove("hidden");
      } else {
        panel.classList.add("hidden");
      }
    });
  }

  /**
   * Initialize map hover events.
   */
  initMapHoverEvents() {
    const mapEl = document.querySelector(
      "#leaflet-map-view-country-hover-map-default"
    );

    if (!mapEl) {
      return;
    }

    const mutationObserver = new MutationObserver(() => {
      const svgPaths = mapEl.querySelectorAll(".leaflet-interactive");
      if (svgPaths.length === 0) {
        return;
      }

      mutationObserver.disconnect();

      const handleHoverInOut = (e) => {
        const groupClass = Array.from(e.currentTarget.classList).find(
          (classname) => classname.startsWith("polygon-group")
        );
        Array.from(mapEl.getElementsByClassName(groupClass)).forEach(
          (everyPath) => {
            if (e.type === "mouseover") {
              everyPath.classList.add("hover");
            }
            if (e.type === "mouseout") {
              everyPath.classList.remove("hover");
            }
          }
        );
      };

      svgPaths.forEach((svgPath) => {
        svgPath.addEventListener("mouseover", handleHoverInOut);
        svgPath.addEventListener("mouseout", handleHoverInOut);
      });
    });

    mutationObserver.observe(mapEl, {
      attributes: false,
      childList: true,
      subtree: true,
    });
  }

  // eslint-disable-next-line valid-jsdoc
  /**
   * Static initialization method
   * Instantiates the component on all matching elements
   */
  static init() {
    const elements = document.querySelectorAll(".map-list-tab-component");

    if (!elements.length) {
      return [];
    }

    // Create array to hold instances (prevents ESLint 'no-new' error)
    const instances = [];

    elements.forEach((element) => {
      // Verify element is valid
      if (element && element instanceof HTMLElement) {
        instances.push(new MapListTab(element));
      }
    });

    return instances;
  }
}

// DOM already loaded (happens if script is loaded asynchronously)
MapListTab.init();
